import React, { createContext, useContext, useState, useEffect } from 'react';
import moment from 'moment';
import API_URL from '../config';
import logger from '../utils/logger';


const DailyCheckInContext = createContext();

export const DailyCheckInProvider = ({ children }) => {
  const [checkIn, setCheckIn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCheckIn = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = moment().format('YYYY-MM-DD');
      
      const response = await fetch(`${API_URL}/api/daily-check-in/${today}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch check-in');
      
      const data = await response.json();
      setCheckIn(data);
    } catch (error) {
      logger.error('Error fetching check-in:', error);
      setError('Failed to fetch check-in');
    } finally {
      setLoading(false);
    }
  };

  const submitCheckIn = async (ratings, timestamp) => {
    try {
      const token = localStorage.getItem('token');
      const today = moment().format('YYYY-MM-DD');
      
      logger.debug('Submitting check-in:', { date: today, ...ratings, timestamp });
      
      const response = await fetch(`${API_URL}/api/daily-check-in`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: today,
          sleep: ratings.sleep,
          mood: ratings.mood,
          energy: ratings.energy,
          stress: ratings.stress,
          confidence: ratings.confidence,
          timestamp
        })
      });

      const responseData = await response.text();
      logger.debug('Server response:', response.status, responseData);

      if (!response.ok) {
        throw new Error(`Failed to submit check-in: ${response.status} ${responseData}`);
      }
      
      const data = JSON.parse(responseData);
      setCheckIn(data);
      return true;
    } catch (error) {
      logger.error('Error submitting check-in:', error);
      setError(error.message);
      return false;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCheckIn();
    } else {
      setLoading(false);
    }
  }, []);

  const value = {
    checkIn,
    loading,
    error,
    submitCheckIn,
    fetchCheckIn
  };

  return <DailyCheckInContext.Provider value={value}>{children}</DailyCheckInContext.Provider>;
};

export const useCheckIn = () => {
  const context = useContext(DailyCheckInContext);
  if (context === undefined) {
    throw new Error('useCheckIn must be used within a DailyCheckInProvider');
  }
  return context;
};
